import Cookies from 'js-cookie';
import { isRunningInFEDSite } from 'utils/fedHelper';

interface AcknowledgementProps {
    el: HTMLElement;
}

const ACKNOWLEDGEMENT_COOKIE_AGE = 60; // days

const setAcknowledgementServerCookie = async({ url }: { url: string }): Promise<boolean> => {
    const fetchUrl = isRunningInFEDSite() ? 'https://httpbin.org/post' : url;
    const result = await fetch(fetchUrl, { credentials: 'include', method: 'POST' });
    return !!result.ok;
};

const setAcknowledgementClientCookie = ({ cookieName }: { cookieName: string }): void => {
    // as per BE request, we will only set the client cookie if we are not in MCC site mode
    const mccBody = document.querySelector('body.mcc');
    if (!mccBody && cookieName) {
        Cookies.set(cookieName, '1', { expires: ACKNOWLEDGEMENT_COOKIE_AGE });
    }
};

const getAcknowledgementClientCookie = ({ cookieName }: { cookieName: string }): string | undefined => {
    return cookieName ? Cookies.get(cookieName) : undefined;
};

const Acknowledgement = ({ el }: AcknowledgementProps): () => void => {
    let cookieName: string | undefined;
    let pingUrl: string | undefined;
    try {
        const data = el.dataset.acknowledgement ? JSON.parse(el.dataset.acknowledgement) : undefined;
        cookieName = data?.cookieName;
        pingUrl = data?.pingUrl;
    } catch (error) {
        console.warn('Invalid acknowledgement data attribute:', error);
    }

    const closeButton = el.querySelector('.close-button');
    const cookie = getAcknowledgementClientCookie({ cookieName: cookieName || '' });

    const showAcknowledgement = (): void => {
        // show the acknowledgement
        el.classList.add('active');
    };

    const isRunningInFed = isRunningInFEDSite();

    const checkAcknowledgementStatus = async(): Promise<void> => {
        // Decision tree for showing acknowledgement:
        // 1. If cookie exists -> don't show (handled earlier)
        // 2. If no cookie and no pingUrl -> show
        // 3. If no cookie and pingUrl exists -> ping first, then decide
        if (!cookie) {
            if (pingUrl) {
                try {
                    const result = await fetch(pingUrl, {
                        method: 'GET',
                        credentials: 'include',
                    });

                    if (result.ok) {
                        const data = await result.json();

                        // Standardise response parsing
                        // if running in FED site and pingUrl is httpbin.org (for testing), we need to check args
                        const pingResponse = isRunningInFed && pingUrl.includes('httpbin.org')
                            ? {
                                success: data.args?.isSuccess === 'true',
                                exist: data.args?.exist === 'true',
                            }
                            : {
                                success: data.isSuccess,
                                exist: data.data?.exist,
                            };

                        if (pingResponse.success && pingResponse.exist) {
                            // acknowledgement is closed, we don't show it
                            // add cookie, so next time we don't need to ping to check
                            setAcknowledgementClientCookie({ cookieName: cookieName || '' });
                        } else {
                            // acknowledgement never closed on other site, so we show it
                            showAcknowledgement();
                        }
                    } else {
                        // request failed, we show the acknowledgement
                        showAcknowledgement();
                    }
                } catch (error) {
                    showAcknowledgement();
                }
            } else {
                // no pingUrl defined, since there's not cookie as welll, we show the acknowledgement
                showAcknowledgement();
            }
        }
    };

    // run checkAcknowledgementStatus asynchronously
    // if it fails, we show the acknowledgement
    checkAcknowledgementStatus().catch(error => {
        console.warn('Failed to check acknowledgement status:', error);
        showAcknowledgement();
    });

    const clickHandler = (): void => {
        el.classList.add('hide');
        setTimeout(() => {
            el.classList.remove('active');
        }, 300);

        // async function to set server cookie
        const setCookies = async(): Promise<void> => {
            await setAcknowledgementServerCookie({ url: pingUrl || '' });
        };

        // set server cookie, so we can check on other sites
        setCookies().finally(() => {
            // once set server cookie done (no matter the result), we can set client cookie
            setAcknowledgementClientCookie({ cookieName: cookieName || '' });
        });
    };

    closeButton?.addEventListener('click', clickHandler);

    // Cleanup function to remove event listeners
    return (): void => {
        closeButton?.removeEventListener('click', clickHandler);
    };
};

export default Acknowledgement;
